import { css } from 'styled-components';
import { ButtonHTMLAttributes } from 'react';

import { styled } from '../../style/mixins/core/styled-wrapper';
import { size as sizeHelper } from '../../style/helpers';
import { ButtonTheme, ButtonStyles } from '../../style/theme';
import {
  border,
  BorderProps,
  color,
  ColorProps,
  flexbox,
  FlexboxProps,
  layout,
  LayoutProps,
  position,
  PositionProps,
  space,
  SpaceProps,
  typography,
  TypographyProps,
} from '../../style/mixins';

export type ButtonBaseProps = SpaceProps &
  BorderProps &
  ColorProps &
  FlexboxProps &
  LayoutProps &
  PositionProps &
  TypographyProps &
  ButtonHTMLAttributes<HTMLButtonElement>;

export const ButtonBase = styled('button')<ButtonBaseProps>`
  ${border}
  ${space}
  ${layout}
  ${position}
  ${color}
  ${typography}
  ${flexbox}
`;
ButtonBase.defaultProps = {
  textDecoration: 'none',
};

const colorStyles = (
  buttonTheme: ButtonTheme,
  variant: ButtonStyles = 'primary',
  inverted = false
) => {
  const buttonBgFg = buttonTheme[variant][inverted ? 'inverted' : 'default'];
  return css`
    background-color: ${buttonBgFg.bg.default};
    color: ${buttonBgFg.fg.default};
    border: 1px solid ${buttonBgFg.border.default};

    &:not([disabled]) {
      &:hover {
        background-color: ${buttonBgFg.bg.hover};
        border-color: ${buttonBgFg.border.hover};
        color: ${buttonBgFg.fg.hover};
      }
    }
  `;
};

interface IButton extends ButtonBaseProps {
  fullWidth?: boolean;
  variant?: ButtonStyles;
  inverted?: boolean;
  size?: 'small' | 'default';
  center?: boolean;
}

export const Button = styled(ButtonBase)<IButton>`
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  font-size: ${({ theme }) => theme.fontSizes.body1};
  transform-origin: 50% 50%;
  transition: 200ms ease;
  border-radius: 4px;
  white-space: nowrap;

  ${({ center }) =>
    center &&
    css`
      margin-left: auto;
      margin-right: auto;
    `}

  ${({ variant, inverted, theme }) =>
    colorStyles(theme.color.button, variant || 'primary', inverted)};

  :active:not(:disabled) {
    transform: scale(0.97);
  }

  @media (prefers-reduced-motion) {
    :active {
      transform: none;
    }
  }

  &[disabled] {
    opacity: 0.3;
    cursor: not-allowed;
  }

  ${({ fullWidth }) =>
    fullWidth &&
    css`
      width: 100%;
    `}
`;
Button.defaultProps = {
  pl: 3,
  pr: 3,
  h: 48,
};

export const ButtonContent = styled('span')`
  display: block;

  & + & {
    margin-left: ${sizeHelper([1])};
  }
`;

export const IconButton = styled(ButtonBase)<ButtonBaseProps>`
  :hover {
    background-color: ${({ theme }) => theme.color.bg.secondary};
  }

  :active {
    transform: scale(0.97);
  }
`;
IconButton.defaultProps = {
  borderRadius: 4,
  p: 1,
  color: 'fg.action',
};

export const FloatingButton = styled(Button)`
  border-radius: 24px;
  font-weight: ${({ theme }) => theme.fontWeights.regular};
  overflow: hidden;
  text-transform: none;
  z-index: 200;
  border: 0;

  &:active:not(:disabled) {
    background-color: ${({ theme }) => theme.color.button.floating.default.bg.default};
    transform: none;
  }
`;
FloatingButton.defaultProps = {
  variant: 'floating',
  position: 'fixed',
  pl: 3,
  pr: 5,
};
